import { useMutation, useQuery } from "@apollo/client/react"
import { useState } from "react"
import { Link } from "react-router-dom"
import { formatDate } from "~/common/date"
import { experienceProfilePath } from "~/common/paths"
import { useDocumentTitle } from "~/common/use-document-title"
import { PageLayout } from "~/layouts/page-layout"
import { Button } from "~/ui/button"
import { GraphqlError } from "~/ui/graphql-error"
import { OPERATIONS, RETRY_REMINDER } from "./calendar-queries"

const dollars = (cents: number) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(cents / 100)
export function OperationsScreen() {
  useDocumentTitle("Usage & reminders")
  const { data, error, loading, refetch } = useQuery(OPERATIONS, { pollInterval: 15000 })
  const [retry, { loading: retrying }] = useMutation(RETRY_REMINDER)
  const [actionError, setActionError] = useState<Error | null>(null)
  const workspace = data?.operationsWorkspace
  const budget = workspace?.budget
  return (
    <PageLayout title="Usage & reminders">
      <div className="mx-auto max-w-5xl space-y-7">
        {error && <GraphqlError error={error} />}
        {loading && !data && <p>Loading usage…</p>}
        {budget && (
          <section className="space-y-4 rounded-2xl border bg-card p-6 sm:p-8">
            <p className="text-xs font-semibold uppercase tracking-widest text-primary">
              Monthly AI allowance · UTC month
            </p>
            <h2 className="text-3xl font-semibold tracking-tight">
              {dollars(budget.accountedCents)}{" "}
              <span className="text-base font-normal text-muted-foreground">
                of {dollars(budget.capCents)}
              </span>
            </h2>
            <progress
              className="h-2 w-full overflow-hidden rounded-full appearance-none border-0 bg-accent [&::-webkit-progress-bar]:bg-accent [&::-webkit-progress-value]:bg-primary [&::-moz-progress-bar]:bg-primary"
              max={budget.capCents}
              value={budget.accountedCents}
              aria-label="Monthly AI allowance used"
            />
            <p className="text-sm leading-6 text-muted-foreground">
              Includes reported API cost where available and conservative reserves elsewhere. These
              are app-tracked costs, not a provider invoice. Activity in other apps, storage and
              hosting are separate. New paid operations stop when the remaining allowance cannot
              cover their reserve.
            </p>
            <p className="text-sm text-muted-foreground">
              Fixed AI subscriptions included: {dollars(budget.fixedCents)}.{" "}
              {budget.fixedCents === 0
                ? "No subscription amount is configured yet; add it to make the allowance reflect those fees."
                : "This amount is reserved at the start of every month."}
            </p>
            {budget.warning && (
              <p role="alert" className="rounded-lg bg-amber-50 p-3 text-sm text-amber-900">
                At least 80% of the monthly allowance is accounted for.
              </p>
            )}
            {budget.uncertainCount > 0 && (
              <p className="text-sm text-amber-800">
                {budget.uncertainCount} operations have pending or uncertain outcomes. Their
                reserves remain counted to prevent accidental overspending.
              </p>
            )}
            <details>
              <summary className="cursor-pointer text-sm font-medium">
                Recent provider operations
              </summary>
              <div className="mt-4 space-y-3">
                {budget.entries.map((entry) => (
                  <div key={entry.id} className="border-t pt-3 text-sm">
                    <div className="flex flex-wrap justify-between gap-2">
                      <p>
                        {entry.provider} · {entry.operation.replaceAll("_", " ")}
                      </p>
                      <p>
                        {dollars(entry.actualCents ?? entry.reservedCents)}{" "}
                        {entry.actualCents == null ? "reserved" : "reported"}
                      </p>
                    </div>
                    <p className="mt-1 text-xs text-muted-foreground">
                      {entry.status} · {formatDate(entry.createdAt)}
                    </p>
                    {entry.error && <p className="mt-2 text-xs text-amber-800">{entry.error}</p>}
                  </div>
                ))}
              </div>
            </details>
          </section>
        )}
        <section className="space-y-4 rounded-2xl border bg-card p-6 sm:p-8">
          <h2 className="font-semibold">My reminders</h2>
          <p className="text-sm text-muted-foreground">
            Friday recording batches and scheduled posting times. No daily topic messages.
          </p>
          <p className="text-sm">
            Slack bot: {workspace?.slackConfigured ? "configured" : "needs a server bot token"} · My
            reminders: {workspace?.profile?.slackRemindersEnabled ? "enabled" : "off"}
          </p>
          <Link
            to={experienceProfilePath({})}
            className="inline-block text-sm text-primary underline"
          >
            Set my Slack destination
          </Link>
          {actionError && <GraphqlError error={actionError} />}
          {!workspace?.reminders.length && (
            <p className="text-sm text-muted-foreground">
              No reminders are due yet. They will appear here when the worker checks your Friday
              time or a scheduled post becomes due.
            </p>
          )}
          {workspace?.reminders.map((reminder) => (
            <article key={reminder.id} className="space-y-2 border-t pt-4">
              <p className="text-sm font-medium">
                {reminder.kind === "recording" ? "Friday recording batch" : "Posting reminder"} ·{" "}
                {reminder.status === "pending"
                  ? "Dashboard reminder"
                  : reminder.status.replaceAll("_", " ")}
              </p>
              <p className="text-sm leading-6">{reminder.message}</p>
              {reminder.error && <p className="text-sm text-amber-800">{reminder.error}</p>}
              {["pending", "failed", "setup_required"].includes(reminder.status) &&
                workspace.slackConfigured &&
                workspace.profile?.slackRemindersEnabled && (
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={retrying}
                    onClick={async () => {
                      setActionError(null)
                      try {
                        await retry({ variables: { input: { id: reminder.id } } })
                        await refetch()
                      } catch (err) {
                        setActionError(
                          err instanceof Error ? err : new Error("Could not retry reminder")
                        )
                      }
                    }}
                  >
                    Retry Slack delivery
                  </Button>
                )}
            </article>
          ))}
        </section>
      </div>
    </PageLayout>
  )
}
